import React, { useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Menu from '@mui/material/Menu';
import { useLogout } from 'ducks/user';
import { useUserContext } from 'common/contexts/UserContext';
import { generatePath, Link, useLocation } from 'react-router-dom';
import _ from 'lodash';
import { ContainerTracking } from 'components/ContainerTracking';
import { User } from 'types/User';
import { brandColors } from 'brandColors';
import { useRegion } from 'common/hooks/region';
import { usePermissions } from 'utils/permissions';
import { USER_MENU_WIDTH } from './utils/config';

// TODO: Replace this with a ThemeProvider
import styles from '../../styles/typography.module.css';
import { getUserProfileItems } from './utils/menus';

interface ProfileLettersProps {
  name: string;
}

const ProfileLetters: React.FC<ProfileLettersProps> = ({ name }) => {
  const arr = name.split('');

  return (
    <>
      {arr[0]}
      {arr[1]}
    </>
  );
};
ProfileLetters.displayName = 'UserProfileItem.ProfileLetters';

interface ProfileDropdownProps {
  anchorEl: HTMLElement | null;
  onClose: (event: React.MouseEvent<HTMLElement>) => void;
  user: User;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ anchorEl, onClose, user }) => {
  const region = useRegion();
  const permissions = usePermissions();
  const handleLogout = useLogout();

  const dropdownItems = useMemo(
    () => _.flatMap(getUserProfileItems(permissions, user), (item) => item.children),
    [permissions, user]
  );

  const open = Boolean(anchorEl);

  return (
    <Menu
      id="basic-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      sx={{
        '.MuiPaper-root': {
          width: `${USER_MENU_WIDTH}px`,
          left: 'unset !important',
          right: '1rem',
          boxShadow: brandColors.boxShadow.md,
        },
        '.MuiList-root': {
          paddingTop: '0px',
        },
      }}
      style={{
        zIndex: '1500',
      }}
      className={styles.font}
    >
      {user?.company && (
        <div className="bg-blue-100 py-4 px-4 border-b border-blue-200">
          <ContainerTracking />
        </div>
      )}
      <ul className="flex flex-col items-start py-2" style={{ width: `${USER_MENU_WIDTH}px` }}>
        {dropdownItems.map((item) => (
          <li
            key={item.page.title}
            onClick={onClose}
            className="w-full px-4 py-2 hover:bg-blue-100"
          >
            <Link to={generatePath(item.page.path, { region: region.route })} className="w-full ">
              <h4 className=" text-gray-900 hover:text-blue-900">{item.page.title}</h4>
            </Link>
          </li>
        ))}
        <li onClick={onClose} className="px-4 py-2 w-full cursor-pointer hover:bg-blue-100">
          <h4 onClick={handleLogout} className="font-medium text-gray-900 hover:text-blue-900">
            Log Out
          </h4>
        </li>
      </ul>
    </Menu>
  );
};
ProfileDropdown.displayName = 'UserProfileItem.ProfileDropdown';

const UserProfileItem: React.FC = () => {
  const { user } = useUserContext();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // TODO: Should we render something nicer if the user is still loading?
  if (!user) {
    return null;
  }

  const { email, company } = user;

  return (
    <>
      <div
        className={`relative flex items-center pl-4 border-l cursor-pointer border-blue-700 pr-1 h-14 hover:bg-darkblue-900 ${
          anchorEl || location.pathname.startsWith('/settings')
            ? 'bg-darkblue-900 text-white hover:bg-darkblue-900'
            : 'hover:bg-darkblue-900'
        }`}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        style={{
          width: USER_MENU_WIDTH,
        }}
      >
        <div className="w-full">
          <div className="flex h-full space-x-2 items-center pr-4">
            {/* Profile Circle */}
            <div
              style={{ height: '40px', width: '40px' }}
              className="bg-orange-50 border border-orange-900 rounded-full flex justify-center items-center"
            >
              <h2 className="font-bold uppercase text-orange-900">
                <ProfileLetters name={company ? company.name : email} />
              </h2>
            </div>
            {/* Profile Infos */}
            <div style={{ width: '155px' }} className="overflow-hidden">
              <h4 className="font-bold text-blue-300 truncate">{company ? company.name : ''}</h4>
              <h5 className="font-medium text-blue-300 truncate">{email}</h5>
            </div>
            <div className="text-blue-300 self-end">
              <KeyboardArrowDownIcon style={{ fontSize: '20px' }} />
            </div>
          </div>
        </div>
      </div>
      <ProfileDropdown anchorEl={anchorEl} onClose={() => setAnchorEl(null)} user={user} />
    </>
  );
};
UserProfileItem.displayName = 'UserProfileItem';

export default UserProfileItem;
